import type { ApiError } from '~/types/types'

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()

  const api = $fetch.create({
    baseURL: config.public.apiBaseUrl,
    credentials: 'include',
    onRequest({ request, options: requestOptions }) {
      // Resolve auth + tenant state at REQUEST time, not at plugin init.
      // useCookie returns the same shared reactive state regardless of where
      // it's called from, but reading inline removes any chance of a stale
      // capture (which was producing 401s on the calls fired immediately
      // after login: setToken updated the cookie, plugin-captured ref read
      // pre-update value).
      const authToken = useAuthToken()
      const tenant = useTenant()

      const incomingHeaders = new Headers(requestOptions.headers as HeadersInit)
      const headers = new Headers(requestOptions.headers as HeadersInit)
      headers.set('Accept', 'application/json')
      headers.set('Accept-Language', (nuxtApp.$i18n as any).locale.value ?? 'en')
      const requestPath = typeof request === 'string' ? request : request.toString()
      const isPublicApi = requestPath.startsWith('/api/public/')

      // Respect an explicitly passed Authorization header (e.g. Google OAuth callback)
      if (!incomingHeaders.has('Authorization')) {
        const token = authToken.token.value
        if (token) {
          headers.set('Authorization', `Bearer ${token}`)
        } else {
          headers.delete('Authorization')
        }
      }

      // Respect explicitly passed X-Tenant-Slug (e.g. post-creation uploads)
      if (!incomingHeaders.has('X-Tenant-Slug')) {
        if (tenant.tenant.value?.slug) {
          headers.set('X-Tenant-Slug', tenant.tenant.value.slug)
        } else {
          headers.delete('X-Tenant-Slug')
        }
      }

      // SSR requests to the API run server-to-server (Node → Laravel), so
      // there's no browser-added Origin header the way a client-side fetch
      // gets one automatically. Backend's TenantDomainFinder resolves public/
      // subdomain-scoped tenant data from Origin (falling back to X-Tenant-Slug
      // only when it carries a bearer token or signed URL — anonymous public
      // requests have neither). Without this, every public endpoint that
      // relies on subdomain resolution (e.g. /api/public/booking/staff) 404s
      // during SSR only, so content silently vanishes before hydration ever
      // gets a chance to retry client-side.
      if (import.meta.server && !incomingHeaders.has('Origin')) {
        const t = tenant.tenant.value
        if (tenant.tenantMode.value === 'pro' && t?.slug) {
          const root = String(config.public.platformRootDomain || 'zerotohero.network')
          headers.set('Origin', `https://${t.slug}.${root}`)
        } else if (tenant.tenantMode.value === 'enterprise' && t?.custom_domain) {
          headers.set('Origin', `https://${t.custom_domain}`)
        }
      }

      requestOptions.headers = headers
    },
    onResponseError({ response }) {
      const data = response._data as Record<string, unknown> | undefined

      // Paywall (EnsureSubscriptionActive). Staff land on Settings → Billing,
      // the one /agent page that stays reachable while the tenant is blocked.
      if (import.meta.client && response.status === 402 && data?.code === 'subscription_blocked') {
        const path = window.location.pathname
        if (path.startsWith('/agent') && !path.startsWith('/agent/settings/billing')) {
          navigateTo({ path: '/agent/settings/billing', query: { blocked: '1' } })
        }
      }
      const normalized: ApiError = {
        status: response.status,
        message: String(data?.message || response.statusText || 'Request failed'),
        errors: (data?.errors as Record<string, string[]>) || undefined
      }

      // Spread the full response body into `data` so callers can read any
      // domain-specific fields the backend returned (e.g. `missing_slots`
      // on a 422 from CaseController::transition). The normalized envelope
      // keys (status/message/errors) still win on the surface.
      throw createError({
        statusCode: normalized.status,
        statusMessage: normalized.message,
        data: { ...(data ?? {}), ...normalized }
      })
    }
  })

  return {
    provide: {
      api
    }
  }
})
